// components/admin/AdminUserLogs.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, Table, Badge, Dropdown, Button, Form, Pagination, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { differenceInMinutes, format } from "date-fns";
import { enUS } from "date-fns/locale";
import { fetchUserLogsAdmin } from "../../http/userLogAPI";

// Твои базовые типы событий (пример — дополни/поменяй своим списком)
const LOG_TYPES = [
    "take_assign",
    "assign_taken",
    "assignment_create",
    "owner_cancel_assign",
    "review_link_received",
    "success_confirm",
    "cancel_reading_exp",
    "cancel_confirm_exp",
    "welcome_bonus_7d",
    "welcome_bonus_3d",
    "welcome_bonus_1d",
    "complaint_status_update",
    "change_tariff",
    "profile",
    "adm_balance_change",
    "SB_Second_Part",
    "referral",
    "Cancel by timeOut Reading",
];

const AdminUserLogs = () => {
    // ===== Фильтры =====
    const [q, setQ] = useState("");         // строковый поиск
    const [userId, setUserId] = useState(""); // фильтр по user_id
    const [logType, setLogType] = useState(""); // дропдаун
    const [autoEnabled, setAutoEnabled] = useState(false);


    // ===== Пагинация =====
    const [page, setPage]   = useState(1);
    const [limit, setLimit] = useState(25);
    const [count, setCount] = useState(0);
    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(count / Math.max(1, limit))),
        [count, limit]
    );

    // ===== Данные =====
    const [logs, setLogs]     = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    // ===== Debounce =====
    const debounceRef = useRef(null);
    const skipNextLoadRef = useRef(true);

    const formatDateWithElapsedTime = (seconds) => {
        if (!seconds && seconds !== 0) return "N/A";
        const date = new Date(Number(seconds) * 1000);
        const now  = new Date();

        const formattedDate = format(date, "dd-MMM-yy", { locale: enUS });

        const diffMinutes = differenceInMinutes(now, date);
        if (diffMinutes < 0) return `${formattedDate} (Future)`;

        const days  = Math.floor(diffMinutes / 1440); // 60*24
        const hours = Math.floor((diffMinutes % 1440) / 60);

        return `${formattedDate} ${days}D:${hours}H`;
    };

    const buildQuery = useCallback(() => {
        const base = {
            page,
            limit,
            q: q?.trim() || undefined,
            user_id: userId?.trim() || undefined,
            log_type: logType || undefined,
        };
        return Object.fromEntries(Object.entries(base).filter(([, v]) => v !== undefined && v !== ""));
    }, [page, limit, q, userId, logType]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const payload = await fetchUserLogsAdmin(buildQuery());
            const items = payload?.items || [];
            setLogs(items);
            setCount(Number(payload?.count || 0));
            setMessage(items.length ? "" : "No logs found");
            setAutoEnabled(true);
        } catch (e) {
            console.error("Logs fetch failed:", e);
            setLogs([]);
            setCount(0);
            setMessage("Failed to fetch logs");
            toast.error("Error fetching logs", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setLoading(false);
        }
    }, [buildQuery]);

    // Пагинация — автозагрузка
    useEffect(() => {
        if (!autoEnabled) return;
        if (skipNextLoadRef.current) {
            skipNextLoadRef.current = false;
            return;
        }

        loadData();
    }, [page, limit, loadData]);

    // Автопоиск при изменении элементов поиска (debounce 350ms)
    useEffect(() => {
        if (!autoEnabled) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            // сбрасываем на первую страницу и загружаем
            setPage(1);
            // сразу грузим с page=1, учитывая новые фильтры
            (async () => {
                setLoading(true);
                try {
                    const payload = await fetchUserLogsAdmin({ ...buildQuery(), page: 1 });
                    const items = payload?.items || [];
                    setLogs(items);
                    setCount(Number(payload?.count || 0));
                    setMessage(items.length ? "" : "No logs found");
                } catch (e) {
                    console.error(e);
                    toast.error("Failed to apply filter", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                } finally {
                    setLoading(false);
                }
            })();
        }, 350);
        return () => clearTimeout(debounceRef.current);
    }, [q, userId, logType]); // изменились элементы поиска → автозапрос

    const resetFilters = useCallback(() => {
        skipNextLoadRef.current = true;
        setQ("");
        setUserId("");
        setLogType("");
        setPage(1);
        setLogs([]);
        setCount(0);
        setMessage("");
        setLoading(false);
    }, []);

    const handlePageChange = useCallback((next) => {
        const p = Math.max(1, Math.min(next, totalPages || 1));
        if (p !== page) setPage(p);
    }, [page, totalPages]);

    const handleLimitChange = useCallback((newLimit) => {
        const l = Math.max(1, Math.min(newLimit || 50, 200));
        if (l !== limit) {
            setLimit(l);
            setPage(1);
        }
    }, [limit]);

    return (
        <Card className="shadow-sm bg-light p-0">
            <Card.Body>
                <div className="d-flex flex-wrap gap-2 mb-3 align-items-end justify-content-start">
                    {/* Дропдаун лог-типа */}
                    <Dropdown onSelect={(value) => setLogType(value || "")}>
                        <Dropdown.Toggle variant="secondary" size="sm" style={{ minWidth: "160px" }}>
                            {logType || "Log Type"}
                        </Dropdown.Toggle>
                        <Dropdown.Menu style={{ maxHeight: 320, overflowY: "auto" }}>
                            <Dropdown.Item eventKey="">— Any —</Dropdown.Item>
                            {LOG_TYPES.map((t) => (
                                <Dropdown.Item key={t} eventKey={t}>{t}</Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>

                    {/* Apply / Reset (на случай ручного триггера) */}
                    <Button variant="success" size="sm" onClick={loadData} disabled={loading}>
                        Apply Filter
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={resetFilters} disabled={loading}>
                        Reset
                    </Button>

                    {/* Строковый поиск */}
                    <div style={{ width: 180 }} className="d-none d-md-block">
                        <Form.Control
                            type="text"
                            className="form-control-sm"
                            placeholder="Search logs…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                    </div>

                    {/* user_id */}
                    <Form.Control
                        type="text"
                        className="form-control-sm"
                        placeholder="User ID"
                        style={{ maxWidth: "100px" }}
                        value={userId}
                        onChange={(e) => setUserId(e.target.value.replace(/[^\d]/g, ""))}
                    />

                    {/* Per Page */}
                    <div className="d-flex align-items-end">
                        <div className="d-flex flex-column">
                            <div className="ms-3">
                                <Form.Select
                                    size="sm"
                                    value={limit}
                                    onChange={(e) => handleLimitChange(Number(e.target.value))}
                                    style={{ width: 60 }}
                                >
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={200}>200</option>
                                </Form.Select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Сообщения/загрузка */}
                {message && <p className="text-muted mt-3 text-center">{message}</p>}
                {loading ? (
                    <div className="d-flex align-items-center gap-2">
                        <Spinner size="sm" /> <small>Loading…</small>
                    </div>
                ) : (
                    <>
                        {/* Таблица */}
                        {logs.length > 0 && (
                            <div style={{ overflowX: "auto", whiteSpace: "nowrap",  lineHeight: "18px" }}>
                                <Table striped bordered hover size="sm" className="table-sm small" style={{ minWidth: 1300 }}>
                                    <colgroup>
                                        <col style={{ width: "30px" }} />   {/* ID */}
                                        <col style={{ width: "30px" }} />   {/* user_id */}

                                        <col style={{ width: "50px" }} />  {/* тип */}
                                        <col style={{ width: "30px" }} />   {/* amount */}
                                        <col style={{ width: "30px" }} />  {/* balance */}

                                        <col style={{ width: "30px" }} />  {/* amz_name */}
                                        <col style={{ width: "900px" }} />  {/* description (раздвинутая) */}
                                        <col style={{ width: "30px" }} />  {/* дата */}
                                        <col style={{ width: "30px" }} />   {/* read */}
                                    </colgroup>
                                    <thead className="small">
                                    <tr className="small">
                                        <th>ID</th>
                                        <th>UID</th>

                                        <th>Type</th>
                                        <th>Amount</th>
                                        <th>Balance</th>

                                        <th>AMZ</th>
                                        <th>Description</th>
                                        <th>Date</th>
                                        <th>Read</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {logs.map((r) => (
                                        <tr key={r.id} className="align-start small">
                                            <td className="py-0 px-1 align-middle">{r.id}</td>
                                            <td className="py-0 px-1 align-middle">{r.user_id}</td>

                                            <td className="py-0 px-1 align-middle">
                                                {r.log_type || "N/A"}
                                            </td>
                                            <td className="py-0 px-1 text-center align-middle">
                                                {r.amount != null
                                                    ? Number(r.amount).toFixed(0) === "0"
                                                        ? "Unchanged"
                                                        : Number(r.amount).toFixed(2)
                                                    : "—"}
                                            </td>

                                            <td className="py-0 px-1 text-center align-middle">
                                                {r.balance_state != null ? Number(r.balance_state).toFixed(0) : "—"}
                                            </td>

                                            <td className="py-0 px-1 align-middle">{r.user_amz_name || "—"}</td>
                                            <td className="py-0 px-1 align-middle" style={{ maxWidth: 360, whiteSpace: "normal" }}>
                                                {r.description || r.description_no_currency || "—"}
                                            </td>
                                            <td className="py-0 px-1 align-middle">{formatDateWithElapsedTime(r.user_log_date)}</td>
                                            <td className="py-0 px-1 align-middle">
                                                {r.is_read ? <Badge bg="success">Yes</Badge> : <Badge bg="warning" text="dark">No</Badge>}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {/* Пагинация */}
                        {logs.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center mt-2">
                                <div className="small text-muted">
                                    Total: {count} • Page {page} of {totalPages}
                                </div>
                                <Pagination size="sm" className="mb-0">
                                    <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page <= 1} />
                                    <Pagination.Item active>{page}</Pagination.Item>
                                    <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages} />
                                </Pagination>
                            </div>
                        )}
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

export default AdminUserLogs;
